import { getResourcesFree } from 'entities/hallmap';
import {
	selectBookingApplicationMod,
	selectBookingThreadMod,
	selectOrderBookingData,
	selectOrderForBooking,
	setBookingThreadMod,
	updateBookingDate,
	updateBookingDuration,
	updateBookingParticipants,
	updateByPass,
	updateHoldResources,
} from 'entities/orders';
import { OrderBookingData } from 'entities/orders/model/types';
import {
	selectActiveBookingTime,
	selectSelectedTimeslot,
	selectTime,
	selectTimesOptions,
	selectTimeslotDate,
	useTimeslot,
	useTimeslotZone,
} from 'entities/toolbar/timeslots';
import { getBookingTimeslots } from 'entities/toolbar/timeslots';
import { useClientForm } from 'features/booking/BookingClientForm';
import { useBookingReset } from 'features/booking/BookingClientForm';
import { useBookingSlots } from 'features/booking/slots';
import { useCallback, useEffect, useRef, useState } from 'react';
import { localStorageItem } from 'shared/lib/core/lib/utils';
import { useAppDispatch, useAppSelector } from 'shared/lib/store';
import { formatTime, timeToMinutes } from 'shared/lib/time';
import { Option, TDay, TNewValue } from 'shared/types';

export const useBookingDateTime = (durationOption: Option | null, setDurationByTime: (timeString: string) => void) => {
	const timeslotDate = useAppSelector(selectTimeslotDate);
	const timeslotTime = useAppSelector(selectTime);
	const timesOptions = useAppSelector(selectTimesOptions);
		const activeBookingTime = useAppSelector(selectActiveBookingTime);
	const isByPass = useAppSelector(selectOrderBookingData)?.bypass;
	// const dateStart = useAppSelector(selectOrderBookingData)?.dt_start;
	const order = useAppSelector(selectOrderForBooking);
	const applictionMod = useAppSelector(selectBookingApplicationMod);
	// const bookingTimes = useAppSelector(selectBookingTimes);
  const { toTimeslot } = useTimeslot();
  

	const [date, setDate] = useState(!order ? timeslotDate : '');
	const [guestCount, setGuest] = useState(0);
	const [time, setTimeString] = useState(!order ? timeslotTime : '');
	const savedData = localStorageItem('booking') as OrderBookingData;

	const { activeBookingBtn } = useClientForm();
	const dispatch = useAppDispatch();
	const dataIsLoaded = useRef(false);
	const { formatISODateBySlotZone, replaceTimeInISODateBySlotZone } =
		useTimeslotZone();

	const [bookingThreadModWasUsed, setUseBookingThreadMod] = useState(false);
	const bookingThreadMod = useAppSelector(selectBookingThreadMod);
	const selectedTimeslot = useAppSelector(selectSelectedTimeslot);

	const { setCurrentActive } = useBookingSlots();

	useEffect(() => {
		if (order && !dataIsLoaded.current) {
      console.log('order', order)
			const isByPass = order?.appointment?.bypass;
			if (isByPass) {
				dispatch(updateByPass({ isByPass }));
			}

			const hold_resources = order?.appointment?.hold_resources;
			dispatch(updateHoldResources({ hold_resources }));

			const date = order.dt_start;
			const formattedTime = formatTime({ time: date, byUtc: true });
			if (formattedTime) {
				setTimeString(formattedTime.time);
			}
			setDate(date);
		}

		if (savedData && !dataIsLoaded.current) {
			const isByPass = savedData?.bypass;
			if (isByPass) {
				dispatch(updateByPass({ isByPass }));
			}

			const hold_resources = savedData?.hold_resources;
			dispatch(updateHoldResources({ hold_resources }));

			const date = savedData.dt_start;
			const formattedTime = formatTime({ time: date, byUtc: true });
			if (formattedTime) {
				setTimeString(formattedTime.time);
			}
			setDate(date);
		}
		dataIsLoaded.current = true;

		//eslint-disable-next-line
	}, [order, dispatch]);

	useBookingReset(() => {
		setDate(timeslotDate);
	})

	const toggleStreamingBookingMode = useCallback(
		(isoDate: string) => {
			if (bookingThreadMod) {
				if (isoDate !== selectedTimeslot) {
					dispatch(setBookingThreadMod({ isWork: false }));
				}
				setUseBookingThreadMod(true);
			} else if (bookingThreadModWasUsed) {
				if (isoDate === selectedTimeslot) {
					dispatch(setBookingThreadMod({ isWork: true }));
				}
			}
		},
		[bookingThreadMod, bookingThreadModWasUsed, selectedTimeslot, dispatch],
	);

	const setSlotDate = useCallback(
		(date: TDay) => {
			if (date && time) {
				let isoDate = formatISODateBySlotZone(date);
				isoDate = replaceTimeInISODateBySlotZone(isoDate, time);
        toTimeslot(isoDate, {});
				setDate(isoDate);

				if (isoDate !== order?.dt_start) {
					activeBookingBtn();
				}

				toggleStreamingBookingMode(isoDate);
				setCurrentActive('');
			}
		},
		[
			time,
			order,
			activeBookingBtn,
			formatISODateBySlotZone,
			toggleStreamingBookingMode,
			replaceTimeInISODateBySlotZone,
			setCurrentActive,
      toTimeslot
		],
	);

	const setGuests = useCallback(
		(option: TNewValue) => {
			if (option) {
				const participants = Number(option.value);
				setGuest(participants);

				if (option.value !== order?.participants_count.toString()) {
					activeBookingBtn();
				}
			}
		},
		[activeBookingBtn, order],
	);

	const setTime = useCallback(
		(option: TNewValue) => {
      if (option) {
				localStorage.setItem('favoriteSavedTime', JSON.stringify(option));

				const timeValue = option.value;
				const dt_slot = replaceTimeInISODateBySlotZone(date, timeValue);
				setTimeString(timeValue);
				setDate(dt_slot);

				setDurationByTime(timeValue);

				const formattedTime = formatTime({
					time: order?.dt_start,
					byUtc: true,
				});

				if (formattedTime && timeValue !== formattedTime.time) {
					activeBookingBtn();
				}

				toggleStreamingBookingMode(dt_slot);
				setCurrentActive('');
			}
		},
		[
			date,
			order,
			activeBookingBtn,
			toggleStreamingBookingMode,
			replaceTimeInISODateBySlotZone,
			setCurrentActive,
			setDurationByTime,
		],
	);

	useEffect(() => {
		if (date && durationOption && guestCount) {
      // console.log('date',date)
			dispatch(updateBookingParticipants({ participants: guestCount }));
			const duration = timeToMinutes(durationOption?.value);

			if (duration !== order?.duration) {
				activeBookingBtn();
			}

			dispatch(updateBookingDuration({ duration }));
			dispatch(updateBookingDate({ date }));
			
			if (!applictionMod) {
				dispatch(
					getBookingTimeslots({
						count: guestCount,
						duration,
						dt_slot: date,
						bypass: isByPass,
					}),
				).then(() => {
					dispatch(
						getResourcesFree({
							dt_requested_start: date,
							duration: duration,
							exclude_appointment: order?.appointment?.id || undefined
						}),
					);
				})
			}
		}
		//eslint-disable-next-line
	}, [order, date, durationOption, guestCount, dispatch, isByPass, applictionMod]);

	// useEffect(() => {
  //   console.log('selectedTimeslot', selectedTimeslot);
  //   console.log('order2', order);
	// 	if (selectedTimeslot) {
  //     setDate(selectedTimeslot)
	// 		const formattedTime = formatTime({
	// 			time: order ? order.dt_start :  selectedTimeslot,
	// 			byUtc: true,
	// 		});
	// 		if (formattedTime) {
	// 			setTimeString(formattedTime.time);
	// 		}
	// 	}
	// 	//eslint-disable-next-line
	// }, [selectedTimeslot]);
	
	return {
		activeBookingTime,
		setGuests,
		setTime,
		setSlotDate,
		setDate,
		timesOptions,
		timeslotDate,
	};
};
